import Vue from 'vue'
import _ from 'lodash'
import moment from 'moment'
import { createHelpers } from 'vuex-map-fields'

import router from '@/router'
import { DATE_FORMAT } from '@/lib/global'

import {
  // GET_QUOTATION_INFORMATION
  GET_QUOTATION_INFORMATION_REQUEST,
  GET_QUOTATION_INFORMATION_SUCCESS,
  GET_QUOTATION_INFORMATION_FAILURE,

  // UPDATE_QUOTATION_INFORMATION
  UPDATE_QUOTATION_INFORMATION_REQUEST,
  UPDATE_QUOTATION_INFORMATION_SUCCESS,
  UPDATE_QUOTATION_INFORMATION_FAILURE,

  // UPDATE_QUOTATION_COMPLETE_STATUS
  UPDATE_QUOTATION_COMPLETE_STATUS_REQUEST,
  UPDATE_QUOTATION_COMPLETE_STATUS_SUCCESS,
  UPDATE_QUOTATION_COMPLETE_STATUS_FAILURE,

  // GET_CUSTOMER_INQUIRY
  GET_CUSTOMER_INQUIRY_REQUEST,
  GET_CUSTOMER_INQUIRY_SUCCESS,
  GET_CUSTOMER_INQUIRY_FAILURE,

  // CHECK_CUSTOMER_AMLO
  CHECK_CUSTOMER_AMLO_REQUEST,
  CHECK_CUSTOMER_AMLO_SUCCESS,
  CHECK_CUSTOMER_AMLO_FAILURE,

  // CHECK_RISK_TVI
  CHECK_RISK_TVI_REQUEST,
  CHECK_RISK_TVI_SUCCESS,
  CHECK_RISK_TVI_FAILURE,

  // CHECK_RISK_PAYER
  CHECK_RISK_PAYER_REQUEST,
  CHECK_RISK_PAYER_SUCCESS,
  CHECK_RISK_PAYER_FAILURE,
} from '@/lib/mutationTypes'
import API, { HOMEPAGE_URL } from '@/lib/api'
import { generateAuthorizationRequestHeader } from '@/lib/services'
import { checkCustomerAmloSuccessMutation } from '@/store/mutations/check-customer-amlo-success.mutation'

const toJson = (response) => response.json()

const { getQuotationFields, updateQuotationFields } = createHelpers({
  getterType: 'getQuotationFields',
  mutationType: 'updateQuotationFields',
})

const state = {
  quotationId: null,
  quotationInformation: {
    data: null,
    isLoading: false,
    isError: false,
  },
  insuredDetail: {
    data: {
      insuredId: null,
      customerIdentity: {
        type: 'idCard',
        number: '',
      },
      customerInformation: {
        namePrefix: '',
        firstname: '',
        lastname: '',
        dob: '',
        age: '-',
        ageMonth: 0,
        ageDay: 0,
        email: '',
        phone: '',
      },
      personalInformation: {
        weight: '',
        height: '',
        gender: 'M',
        occupation: '',
      },
      isGetCustomerIdentitySuccess: false,
      isCheckAmloSuccess: false,
      isPassAmlo: null,
      isValidateInformationSuccess: false,
    },
    isLoading: false,
    isError: false,
  },
  payerInformation: {
    data: {
      payerId: null,
      isPaidByInsuredPerson: false,
      customerIdentity: {
        type: 'idCard',
        number: '',
      },
      customerInformation: {
        namePrefix: '',
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
      },
      isGetCustomerIdentitySuccess: false,
      isCheckAmloSuccess: false,
      isPassAmlo: null,
      isValidateInformationSuccess: false,
    },
    isLoading: false,
    isError: false,
  },
  beneficiary: {
    type: 'heir',
    firstPerson: {
      namePrefix: '',
      firstname: '',
      lastname: '',
    },
    secondPerson: {
      namePrefix: '',
      firstname: '',
      lastname: '',
    },
  },
  insurancePlan: {
    planType: null, // Classic Care or Extra Care
    opdType: null,
    isCheckIPD: true,
    isCheckOPD: false,
    isCheckHIB: false,
    isCheckCancer: false,
    isCheckCI: false,
    deductible: null,
  },
  isCompleted: false,
  isDisabledForm: false,
  isOffsideSalePersonalQuotation: false,
}

const mutations = {
  // GET_QUOTATION_INFORMATION
  [GET_QUOTATION_INFORMATION_REQUEST]: (state) => {
    state.quotationInformation.isLoading = true
    state.quotationInformation.isError = false
  },
  [GET_QUOTATION_INFORMATION_SUCCESS]: (state, payload) => {
    const {
      quotation,
      insuredPerson,
      payer,
      beneficiaryPerson1,
      beneficiaryPerson2,
      documentProcessStatus,
    } = payload

    const quotationId = _.get(quotation, 'quotationId', null)
    const quotationPayerId = _.get(quotation, 'payerId', null)
    const isPaidByInsuredPerson = _.get(quotation, 'isPaidByInsuredPerson', false)
    const isBeneficiaryLegalHeir = _.get(quotation, 'isBeneficiaryLegalHeir', false)
    const planPackageSchemeId = _.get(quotation, 'planPackageSchemeId', null)
    const deductId = _.get(quotation, 'deductId', null)
    const documentProcessStatusId = _.get(documentProcessStatus, 'id', null)
    const isDocumentCompleted = documentProcessStatusId === 3

    const firstPersonNamePrefix = _.get(beneficiaryPerson1, 'title.id', null)
    const firstPersonFirstname = _.get(beneficiaryPerson1, 'firstname', null)
    const firstPersonLastname = _.get(beneficiaryPerson1, 'lastname', null)

    const secondPersonNamePrefix = _.get(beneficiaryPerson2, 'title.id', null)
    const secondPersonFirstname = _.get(beneficiaryPerson2, 'firstname', null)
    const secondPersonLastname = _.get(beneficiaryPerson2, 'lastname', null)

    // Insured Person Information
    const insuredDetail = {
      id: _.get(insuredPerson, 'personId', null),
      namePrefix: _.get(insuredPerson, 'title.name', null),
      firstname: _.get(insuredPerson, 'firstname', null),
      lastname: _.get(insuredPerson, 'lastname', null),
      identityTypeId: _.get(insuredPerson, 'identityTypeId', 'idCard'),
      identityNumber: _.get(insuredPerson, 'identityNumber', ''),
      birthDate: _.get(insuredPerson, 'birthDate', null),
      email: _.get(insuredPerson, 'email', null),
      weight: _.get(insuredPerson, 'weight', null),
      height: _.get(insuredPerson, 'height', null),
      gender: _.get(insuredPerson, 'sex', null),
      occupationId: _.get(insuredPerson, 'occupation.id', null),
      amloRiskLevel: _.get(insuredPerson, 'amloRiskLevel', null),
      phone: _.get(insuredPerson, 'mainMobileTel', null),
    }
    const dob = moment(insuredDetail.birthDate)
    const age = moment().diff(dob, 'years')
    const ageMonth = moment().diff(dob, 'months') % 12
    const ageDay = moment().diff(dob, 'days')

    state.quotationId = quotationId

    state.quotationInformation = {
      data: payload,
      isLoading: false,
      isError: false,
    }

    const checkAmloLevel = (level) => {
      if (level === 0) {
        return null
      }

      if (level === 4) {
        return false
      }

      return true
    }

    // Payer Information
    const payerInformation = {
      id: _.get(payer, 'id', null) || quotationPayerId,
      namePrefix: _.get(payer, 'title.name', null),
      firstname: _.get(payer, 'firstname', null),
      lastname: _.get(payer, 'lastname', null),
      identityTypeId: _.get(payer, 'identityTypeId', null),
      identityNumber: _.get(payer, 'identityNumber', null),
      email: _.get(payer, 'email', null),
      phone: _.get(payer, 'mainMobileTel', null),
      amloRiskLevel: _.get(payer, 'amloRiskLevel', null),
    }

    const isHasCustomerInquiry =
      insuredDetail.id && insuredDetail.firstname && insuredDetail.lastname

    const isHasPersonalInformation =
      insuredDetail.weight &&
      insuredDetail.height &&
      insuredDetail.gender &&
      insuredDetail.occupationId

    state.insuredDetail.data = {
      insuredId: insuredDetail.id,
      customerIdentity: {
        type: insuredDetail.identityTypeId === 2 ? 'passport' : 'idCard',
        number: insuredDetail.identityNumber,
      },
      customerInformation: {
        namePrefix: insuredDetail.namePrefix,
        firstname: insuredDetail.firstname,
        lastname: insuredDetail.lastname,
        dob: insuredDetail.birthDate
          ? moment(insuredDetail.birthDate).add(543, 'years').format(DATE_FORMAT)
          : '',
        age: _.isNaN(age) ? '-' : age,
        ageMonth,
        ageDay,
        email: insuredDetail.email,
        phone: insuredDetail.phone,
      },
      personalInformation: {
        weight: insuredDetail.weight,
        height: insuredDetail.height,
        gender: insuredDetail.gender,
        occupation: insuredDetail.occupationId,
      },
      isGetCustomerIdentitySuccess: payerInformation.id || isHasCustomerInquiry,
      isCheckAmloSuccess: payerInformation.id || isHasCustomerInquiry,
      isPassAmlo: payerInformation.id || checkAmloLevel(insuredDetail.amloRiskLevel),
      isValidateInformationSuccess:
        payerInformation.id || (insuredDetail.id && isHasPersonalInformation),
    }

    state.payerInformation.data = {
      payerId: payerInformation.id,
      isPaidByInsuredPerson: isPaidByInsuredPerson || false,
      customerIdentity: {
        type: payerInformation.identityTypeId === 2 ? 'passport' : 'idCard',
        number: payerInformation.identityNumber,
      },
      customerInformation: {
        namePrefix: payerInformation.namePrefix,
        firstname: payerInformation.firstname,
        lastname: payerInformation.lastname,
        email: payerInformation.email,
        phone: payerInformation.phone,
      },
      isGetCustomerIdentitySuccess: payerInformation.id,
      isCheckAmloSuccess: payerInformation.id,
      isPassAmlo: checkAmloLevel(payerInformation.amloRiskLevel),
      isValidateInformationSuccess: payerInformation.id,
    }

    const isNullBeneficiaryLegalHeir = _.isNull(isBeneficiaryLegalHeir)
    const beneficiaryType = isBeneficiaryLegalHeir ? 'heir' : 'beneficiary'

    state.beneficiary = {
      type: isNullBeneficiaryLegalHeir ? 'heir' : beneficiaryType,
      firstPerson: {
        namePrefix: firstPersonNamePrefix,
        firstname: firstPersonFirstname,
        lastname: firstPersonLastname,
      },
      secondPerson: {
        namePrefix: secondPersonNamePrefix,
        firstname: secondPersonFirstname,
        lastname: secondPersonLastname,
      },
    }

    state.insurancePlan = {
      ...state.insurancePlan,
      planType: planPackageSchemeId,
      deductible: deductId,
    }

    state.isCompleted = isDocumentCompleted
    state.isDisabledForm = isDocumentCompleted
  },
  [GET_QUOTATION_INFORMATION_FAILURE]: (state) => {
    state.quotationInformation.isLoading = false
    state.quotationInformation.isError = true
  },

  // UPDATE_QUOTATION_INFORMATION
  [UPDATE_QUOTATION_INFORMATION_REQUEST]: (state) => {
    state.quotationInformation.isLoading = true
    state.quotationInformation.isError = false
  },
  [UPDATE_QUOTATION_INFORMATION_SUCCESS]: (state, payload) => {
    const {
      quotation: { quotation_id: quotationId },
    } = payload

    state.quotationId = quotationId

    state.quotationInformation.isLoading = false
    state.quotationInformation.isError = false
  },
  [UPDATE_QUOTATION_INFORMATION_FAILURE]: (state) => {
    state.quotationInformation.isLoading = false
    state.quotationInformation.isError = true
  },

  // UPDATE_QUOTATION_COMPLETE_STATUS
  [UPDATE_QUOTATION_COMPLETE_STATUS_REQUEST]: (state) => {
    state.quotationInformation.isLoading = true
    state.quotationInformation.isError = false
  },
  [UPDATE_QUOTATION_COMPLETE_STATUS_SUCCESS]: (state, payload) => {
    const { quotationId } = payload

    state.quotationId = quotationId
    state.isCompleted = true
    state.isDisabledForm = true

    state.quotationInformation.isLoading = false
    state.quotationInformation.isError = false
  },
  [UPDATE_QUOTATION_COMPLETE_STATUS_FAILURE]: (state) => {
    state.quotationInformation.isLoading = false
    state.quotationInformation.isError = true
  },

  // GET_CUSTOMER_INQUIRY
  [GET_CUSTOMER_INQUIRY_REQUEST]: (state, payload) => {
    const { field } = payload

    state[field].isLoading = true
    state[field].isError = false
  },
  [GET_CUSTOMER_INQUIRY_SUCCESS]: (state, payload) => {
    const { field, result } = payload
    const { profile, emails, mobiles } = result.customers[0]
    const { namePrefix, firstname, lastname, birthdate } = profile

    const dob = moment(birthdate)
    const age = moment().diff(dob, 'years')
    const ageMonth = moment().diff(dob, 'months') % 12
    const ageDay = moment().diff(dob, 'days')

    const emailAddress = _.get(emails, '[0].emailAddress', '')
    const phoneNumber = _.get(mobiles, '[0].phone', '')

    const personalInformation = [
      {
        name: 'ชื่อ',
        value: firstname,
      },
      {
        name: 'นามสกุล',
        value: lastname,
      },
      {
        name: 'วันเกิด',
        value: birthdate,
      },
      {
        name: 'อีเมล',
        value: emailAddress,
      },
      {
        name: 'เบอร์โทรศัพท์',
        value: phoneNumber,
      },
    ]

    const isHasFullInformation = _.every(personalInformation, (data) => data.value)

    if (!isHasFullInformation) {
      const familyId = _.get(state.quotationInformation.data, 'familyQuotation.familyId', null)

      const errorPersonalInformationField = _.filter(personalInformation, (data) => !data.value)
      const concatErrorMessageField = _.chain(errorPersonalInformationField)
        .map((data) => data.name)
        .join(', ')
        .value()

      Vue.swal({
        type: 'error',
        title: `ขออภัย ไม่พบข้อมูล ${concatErrorMessageField} ของท่านในระบบ กรุณาแจ้งพนักงานเพื่อปรับปรุงข้อมูลของท่านเข้าสู่ระบบ RM Profile ของ TMB ก่อนทำรายการต่อไป`,
      }).then(() => {
        if (familyId) {
          router.push(`/insurance_family/${familyId}`)
        } else {
          router.push('/home')
        }
      })

      state.isDisabledForm = true
    }

    state[field].customerInquiry = {
      data: result.customers[0],
      isLoading: false,
      isError: false,
    }

    state[field].data.customerInformation = {
      namePrefix,
      firstname,
      lastname,
      dob: dob.add(543, 'years').format(DATE_FORMAT),
      age,
      ageMonth,
      ageDay,
      email: emailAddress,
      phone: phoneNumber,
    }

    state[field].isLoading = false
    state[field].isError = false

    state[field].data.isGetCustomerIdentitySuccess = true
  },
  [GET_CUSTOMER_INQUIRY_FAILURE]: (state, payload) => {
    const { field } = payload

    state[field].isLoading = false
    state[field].isError = true
  },

  // CHECK_CUSTOMER_AMLO
  [CHECK_CUSTOMER_AMLO_REQUEST]: (state, payload) => {
    const { field } = payload

    state[field].isLoading = true
    state[field].isError = false
  },
  [CHECK_CUSTOMER_AMLO_SUCCESS]: checkCustomerAmloSuccessMutation,
  [CHECK_CUSTOMER_AMLO_FAILURE]: (state, payload) => {
    const { field } = payload

    state[field].isLoading = false
    state[field].isError = true
  },

  // CHECK_RISK_TVI
  [CHECK_RISK_TVI_REQUEST]: (state) => {
    state.insuredDetail.isLoading = true
    state.insuredDetail.isError = false
  },
  [CHECK_RISK_TVI_SUCCESS]: (state, payload) => {
    const quotationId = _.get(payload, 'quotationId', null)
    const insuredId = _.get(payload, 'insuredId', null)
    const isNotBlacklist = _.get(payload, 'blacklistInfo.isNotBlacklist', false)

    const { name: routeName, path: routePath } = router.currentRoute

    state.quotationId = quotationId
    state.insuredDetail.data.insuredId = insuredId
    state.insuredDetail.data.isValidateInformationSuccess = isNotBlacklist

    state.insuredDetail.isLoading = false
    state.insuredDetail.isError = false

    state.isDisabledForm = !isNotBlacklist

    if (routeName === 'quotation_personal' && quotationId) {
      router.push(`${routePath}/${quotationId}`)
    }
  },
  [CHECK_RISK_TVI_FAILURE]: (state) => {
    state.insuredDetail.isLoading = false
    state.insuredDetail.isError = true
  },

  // CHECK_RISK_PAYER
  [CHECK_RISK_PAYER_REQUEST]: (state) => {
    state.payerInformation.isLoading = true
    state.payerInformation.isError = false
  },
  [CHECK_RISK_PAYER_SUCCESS]: (state, payload) => {
    const { payerId, riskInfo } = payload

    state.payerInformation.data.payerId = payerId
    state.payerInformation.data.isCheckAmloSuccess = true
    state.payerInformation.data.isPassAmlo = riskInfo.isAllowed
    state.payerInformation.data.isValidateInformationSuccess = true

    state.payerInformation.isLoading = false
    state.payerInformation.isError = false

    state.isDisabledForm = !riskInfo.isAllowed
  },
  [CHECK_RISK_PAYER_FAILURE]: (state) => {
    state.payerInformation.isLoading = false
    state.payerInformation.isError = true
  },
  updateQuotationFields,
  IS_OFFSIDE_SALE_PERSONAL: (state, payload) => {
    const { isOffsideSale } = payload
    state.isOffsideSalePersonalQuotation = isOffsideSale
  },
}

const actions = {
  getQuotationInformation({ commit }, quotationId) {
    commit(GET_QUOTATION_INFORMATION_REQUEST)

    Vue.http
      .get(API.QUOTATION.GET_QUOTATION_INFORMATION(quotationId), {
        headers: {
          ...generateAuthorizationRequestHeader(),
        },
      })
      .then((res) => res.json())
      .then(
        (response) => commit(GET_QUOTATION_INFORMATION_SUCCESS, response.result),
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(GET_QUOTATION_INFORMATION_FAILURE)
          }

          if (status === 500) {
            commit(GET_QUOTATION_INFORMATION_FAILURE)
          }
        },
      )
  },
  updateQuotationInformation({ commit, dispatch }, { quotationId, data }) {
    commit(UPDATE_QUOTATION_INFORMATION_REQUEST)
    const {
      QUOTATION: { UPDATE_IS_OFFSIDE_SALE },
    } = API
    const { isOffsideSale } = data
    const headers = {
      ...generateAuthorizationRequestHeader(),
    }

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<h5 class="m-b-md">กำลังทำการบันทึกข้อมูล</h5><div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    Vue.http
      .put(UPDATE_IS_OFFSIDE_SALE(quotationId), { isOffsideSale }, { headers })
      .then(toJson)
      .then((result) => {
        const { statusCode } = result
        if (statusCode === 200) {
          console.info('Update quotation is offside sale successful')
        } else {
          console.error('Update quotation is offside sale failed')
        }
      })

    Vue.http
      .put(API.QUOTATION.UPDATE_QUOTATION_INFORMATION(quotationId), data, { headers })
      .then(toJson)
      .then(
        (response) => {
          commit(UPDATE_QUOTATION_INFORMATION_SUCCESS, response.result)

          this._vm.$swal.close()

          router.push(`/quotation-confirmation/${quotationId}`)
        },
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(UPDATE_QUOTATION_INFORMATION_FAILURE)
          }

          if (status === 500) {
            commit(UPDATE_QUOTATION_INFORMATION_FAILURE)
          }
        },
      )
  },
  updateQuotationCompleteStatus({ commit, dispatch }, quotationId) {
    commit(UPDATE_QUOTATION_COMPLETE_STATUS_REQUEST)

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<h5 class="m-b-md">กำลังทำการบันทึกข้อมูล</h5><div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    Vue.http
      .post(
        API.QUOTATION.UPDATE_QUOTATION_COMPLETE_STATUS(quotationId),
        {},
        {
          headers: {
            ...generateAuthorizationRequestHeader(),
          },
        },
      )
      .then((res) => res.json())
      .then(
        (response) => {
          commit(UPDATE_QUOTATION_COMPLETE_STATUS_SUCCESS, response.result)

          dispatch('createApplicationDocument', quotationId)
        },
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(UPDATE_QUOTATION_COMPLETE_STATUS_FAILURE)
          }

          if (status === 500) {
            commit(UPDATE_QUOTATION_COMPLETE_STATUS_FAILURE)
          }
        },
      )
  },
  createApplicationDocument({ state, commit }, quotationId) {
    commit(UPDATE_QUOTATION_INFORMATION_REQUEST)

    Vue.http
      .put(
        API.APPLICATION.CREATE_APPLICATION(quotationId),
        {},
        {
          headers: {
            ...generateAuthorizationRequestHeader(),
          },
        },
      )
      .then((res) => res.json())
      .then(
        (response) => {
          commit(UPDATE_QUOTATION_INFORMATION_SUCCESS, response.result)

          this._vm.$swal.close()

          const familyId = _.get(state.quotationInformation.data, 'familyQuotation.familyId', null)

          if (familyId) {
            router.push(`/insurance_family/${familyId}`)
          } else {
            router.push(`/insurance_personal/${response.result.applicationId}`)
          }
        },
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(UPDATE_QUOTATION_INFORMATION_FAILURE)
          }

          if (status === 500) {
            commit(UPDATE_QUOTATION_INFORMATION_FAILURE)
          }
        },
      )
  },
  getCustomerInquiryInformation({ commit, state }, { field, cardType, citizenId }) {
    commit(GET_CUSTOMER_INQUIRY_REQUEST, { field })

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<h5 class="m-b-md">กำลังค้นหาข้อมูล</h5><div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    Vue.http
      .get(API.QUOTATION.CUSTOMER_INQUIRY(cardType, citizenId), {
        headers: {
          ...generateAuthorizationRequestHeader(),
        },
      })
      .then((res) => res.json())
      .then(
        (response) => {
          this._vm.$swal.close()

          commit(GET_CUSTOMER_INQUIRY_SUCCESS, { field, result: response.result })
        },
        (error) => {
          this._vm.$swal.close()

          const {
            status,
            body: { message },
          } = error

          if (status === 400) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัยระบบไม่พบข้อมูลของท่าน',
            })

            commit(GET_CUSTOMER_INQUIRY_FAILURE, { field })
          }

          if (status === 401) {
            this.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 412) {
            const familyId = _.get(
              state.quotationInformation.data,
              'familyQuotation.familyId',
              null,
            )

            Vue.swal({
              type: 'error',
              title: `ขออภัย ที่อยู่ของท่านไม่ถูกต้อง เนื่องจาก ${
                message || ''
              } กรุณาแจ้งพนักงานเพื่อปรับปรุงข้อมูลของท่านเข้าสู่ระบบ RM Profile ของ TMB ก่อนทำรายการต่อไป`,
            }).then(() => {
              if (familyId) {
                router.push(`/insurance_family/${familyId}`)
              } else {
                router.push('/home')
              }
            })

            commit(GET_CUSTOMER_INQUIRY_FAILURE, { field })
          }

          if (status === 500) {
            commit(GET_CUSTOMER_INQUIRY_FAILURE, { field })
          }
        },
      )
  },
  checkCustomerAmlo({ commit }, { field, cardType, citizenId }) {
    commit(CHECK_CUSTOMER_AMLO_REQUEST, { field })

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<h5 class="m-b-md">กำลังทำการตรวจสอบ AMLO</h5><div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    Vue.http
      .post(
        API.QUOTATION.CHECK_RISK_TMB,
        { cardType, citizenId },
        {
          headers: {
            ...generateAuthorizationRequestHeader(),
          },
        },
      )
      .then((res) => res.json())
      .then(
        (response) => {
          this._vm.$swal.close()

          commit(CHECK_CUSTOMER_AMLO_SUCCESS, { field, result: response })
        },
        (error) => {
          this._vm.$swal.close()

          const {
            status,
            body: { message },
          } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            this._vm.$swal({
              type: 'error',
              title: message || 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
            })

            commit(CHECK_CUSTOMER_AMLO_FAILURE, { field })
          }

          if (status === 500) {
            commit(CHECK_CUSTOMER_AMLO_FAILURE, { field })
          }
        },
      )
  },
  checkRiskTvi({ commit }, { quotationId, requestBody }) {
    commit(CHECK_RISK_TVI_REQUEST)

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<h5 class="m-b-md">กำลังทำการตรวจสอบเงื่อนไขกรมธรรม์</h5><div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    const generateCheckRiskTviEndpoint = () => {
      if (quotationId) {
        return `${API.QUOTATION.CHECK_RISK_TVI}/${quotationId}`
      }

      return API.QUOTATION.CHECK_RISK_TVI
    }

    Vue.http
      .post(generateCheckRiskTviEndpoint(), requestBody, {
        headers: {
          ...generateAuthorizationRequestHeader(),
        },
      })
      .then((res) => res.json())
      .then(
        (response) => {
          this._vm.$swal.close()

          const isNotBlacklist = _.get(response.result, 'blacklistInfo.isNotBlacklist', false)

          if (!isNotBlacklist) {
            this._vm.$swal({
              type: 'error',
              title:
                'การตรวจสอบสิทธิ์กรมธรรม์ ขออภัย ข้อมูลของท่านไม่ตรงกับข้อกำหนดและเงื่อนไขของบริษัทประกันภัยไทยวิวัฒน์',
            })
          }

          commit(CHECK_RISK_TVI_SUCCESS, response.result)
        },
        (error) => {
          this._vm.$swal.close()

          const {
            status,
            body: { message },
          } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            this._vm.$swal({
              type: 'error',
              title: message || 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
            })

            commit(CHECK_RISK_TVI_FAILURE)
          }

          if (status === 500) {
            commit(CHECK_RISK_TVI_FAILURE)
          }
        },
      )
  },
  checkRiskPayer({ commit, state }, data) {
    commit(CHECK_RISK_PAYER_REQUEST)

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<h5 class="m-b-md">กำลังทำการตรวจสอบ AMLO</h5><div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    Vue.http
      .post(API.QUOTATION.CHECK_RISK_PAYER(state.quotationId), data, {
        headers: {
          ...generateAuthorizationRequestHeader(),
        },
      })
      .then((res) => res.json())
      .then(
        (response) => {
          this._vm.$swal.close()

          commit(CHECK_RISK_PAYER_SUCCESS, response)
        },
        (error) => {
          this._vm.$swal.close()

          const {
            status,
            body: { message },
          } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            this._vm.$swal({
              type: 'error',
              title: message || 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
            })

            commit(CHECK_RISK_PAYER_FAILURE)
          }

          if (status === 500) {
            commit(CHECK_RISK_PAYER_FAILURE)
          }
        },
      )
  },
  fetchIsOffsideSaleQuotation: function ({ commit }, quotationId) {
    const {
      QUOTATION: { GET_QUOTATION_COLUMNS },
    } = API
    const headers = {
      ...generateAuthorizationRequestHeader(),
    }
    const url = GET_QUOTATION_COLUMNS(quotationId, 'is_offside_sale')

    Vue.http
      .get(url, { headers })
      .then(toJson)
      .then((result) => {
        const { statusCode } = result
        if (statusCode === 200) {
          const {
            result: {
              quotation: { is_offside_sale: isOffsideSale },
            },
          } = result
          commit('IS_OFFSIDE_SALE_PERSONAL', { isOffsideSale })
        } else {
          console.error('Get is offside sale quotation failed')
        }
      })
  },
}

const getters = {
  isOffsideSalePersonal: (state) => state.isOffsideSalePersonalQuotation,

  // Insured Detail
  insuredDetailCustomerInquiryInformation: (state) => state.insuredDetail.customerInquiry.data,
  insuredDetailCustomerInquiryIsError: (state) => state.insuredDetail.customerInquiry.isError,
  insuredDetailCustomerAmloInformation: (state) => state.insuredDetail.customerAmlo.data,
  insuredDetailCustomerAmloIsError: (state) => state.insuredDetail.customerAmlo.isError,
  tviRiskInformation: (state) => state.tviRiskInformation.data,
  createdQuotationInformation: (state) => state.createdQuotation.data,

  // Payer Information
  payerInformationCustomerInquiryInformation: (state) =>
    state.payerInformation.customerInquiry.data,
  payerInformationCustomerInquiryIsError: (state) => state.payerInformation.customerInquiry.isError,
  payerInformationCustomerAmloInformation: (state) => state.payerInformation.customerAmlo.data,
  payerInformationCustomerAmloIsError: (state) => state.payerInformation.customerAmlo.isError,
  getQuotationFields,
}

export default {
  state,
  getters,
  mutations,
  actions,
}
